import React, {useState, useEffect, useRef} from 'react';
import {useRouter} from 'next/router';
import {useRecruitmentStore} from '@stores/useRecruitmentStore';
import RecruitmentService from '@services/recruitment.service';
import {DatePicker, ConfigProvider} from 'antd';
import {Loading3QuartersOutlined} from '@ant-design/icons';
import viVN from 'antd/lib/locale/vi_VN';
import moment from 'moment';
import 'moment/locale/vi';
import styles from './ApplicationForm.module.css';
import {getLocaleContent} from '@helper/locale-content.js';

moment.locale('vi');

// Giới hạn kích thước file CV tối đa 3MB
const MAX_FILE_SIZE = 3 * 1024 * 1024;
// Các định dạng file CV được chấp nhận
const ALLOWED_FILE_TYPES = ['application/pdf'];

/**
 * Component form ứng tuyển
 * Hiển thị dưới dạng sidebar (desktop) hoặc modal (mobile/tablet)
 * @param {Object} recruitment - Thông tin vị trí tuyển dụng
 * @param {boolean} isVisible - Trạng thái hiển thị form
 * @param {Function} onClose - Callback khi đóng form
 * @param {boolean} isModal - Chế độ hiển thị (true: modal, false: sidebar)
 */
export default function ApplicationForm({recruitment, isVisible, onClose, isModal = false}) {
    const router = useRouter();
    const {locale} = router;

    console.log(recruitment);

    const {
        recruitments,
        isLoading: isLoadingRecruitments,
        setRecruitments,
        setLoading: setLoadingRecruitments,
        getGroupedRecruitmentOptions,
    } = useRecruitmentStore();

    const [formData, setFormData] = useState({
        full_name: '',
        phone_number: '',
        email: '',
        address: '',
        birth_of_date: '',
        position_applied_for: recruitment?.recruitment_id || '',
        upload_cv: '',
        content: '',
    });
    const [birthDate, setBirthDate] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [cvFile, setCvFile] = useState(null);
    const fileInputRef = useRef(null);

    // Lấy danh sách vị trí tuyển dụng cho dropdown
    useEffect(() => {
        if (!locale || recruitments.length > 0) return;

        const fetchRecruitments = async () => {
            setLoadingRecruitments(true);
            try {
                const recruitmentService = new RecruitmentService();
                const res = await recruitmentService.getRecruitmentList({
                    locale,
                    pageIndex: 1,
                    pageSize: 100,
                });

                const items = res.data || [];
                const total = res.total || items.length;
                setRecruitments(items, total, 1, 100);
            } catch (error) {
                console.error('Error fetching recruitments:', error);
                setLoadingRecruitments(false);
            }
        };

        fetchRecruitments();
    }, [locale]);

    // Tự động chọn vị trí tuyển dụng hiện tại
    useEffect(() => {
        if (recruitment?.recruitment_id) {
            setFormData(prev => ({
                ...prev,
                position_applied_for: recruitment.recruitment_id,
            }));
        }
    }, [recruitment]);

    // Đóng sidebar khi resize xuống mobile (chỉ áp dụng cho chế độ sidebar)
    useEffect(() => {
        if (isModal) return;

        const handleResize = () => {
            if (window.innerWidth <= 768 && isVisible) {
                onClose();
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isVisible, onClose, isModal]);

    // Xử lý thay đổi giá trị input
    const handleChange = e => {
        const {name, value} = e.target;

        if (name === 'phone_number') {
            const phoneValue = value.replace(/[^0-9]/g, '').slice(0, 11);
            setFormData(prev => ({...prev, [name]: phoneValue}));
        } else {
            setFormData(prev => ({...prev, [name]: value}));
        }

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    // Chặn nhập ký tự không phải số vào trường số điện thoại
    const handlePhoneKeyDown = e => {
        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Escape', 'Enter'];
        if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
        if (!/[0-9]/.test(e.key)) e.preventDefault();
    };

    // Xử lý thay đổi ngày sinh
    const handleDateChange = date => {
        setBirthDate(date);
        const isoDate = date ? date.format('YYYY-MM-DD') : '';
        setFormData(prev => ({...prev, birth_of_date: isoDate}));
        if (errors.birth_of_date) {
            setErrors(prev => ({...prev, birth_of_date: ''}));
        }
    };

    // Xử lý upload file CV
    const handleFileChange = e => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            setErrors(prev => ({...prev, upload_cv: getLocaleContent(locale, 'page.recruitment.err_mime_type')}));
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setErrors(prev => ({...prev, upload_cv: getLocaleContent(locale, 'page.recruitment.err_max_size')}));
            return;
        }

        setCvFile(file);
        setFormData(prev => ({...prev, upload_cv: file.name}));
        setErrors(prev => ({...prev, upload_cv: ''}));
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Reset form về trạng thái ban đầu
    const resetForm = () => {
        setFormData({
            full_name: '',
            email: '',
            phone_number: '',
            address: '',
            birth_of_date: '',
            position_applied_for: recruitment?.recruitment_id || '',
            upload_cv: '',
            content: '',
        });
        setCvFile(null);
        setBirthDate(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    /**
     * Validate form trước khi submit
     * @returns {boolean} true nếu form hợp lệ, false nếu có lỗi
     */
    const validateForm = () => {
        const newErrors = {};

        if (!formData.full_name.trim()) {
            newErrors.full_name = getLocaleContent(locale, 'page.booking-form.validation.full-name-required');
        } else if (formData.full_name.length < 2) {
            newErrors.full_name = getLocaleContent(locale, 'page.booking-form.validation.name-short');
        }

        if (!formData.email.trim()) {
            newErrors.email = getLocaleContent(locale, 'page.booking-form.validation.email-required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = getLocaleContent(locale, 'page.booking-form.validation.email-matches');
        }

        if (!formData.phone_number.trim()) {
            newErrors.phone_number = getLocaleContent(locale, 'page.booking-form.validation.phone-required');
        } else if (!/^[0-9]{10,11}$/.test(formData.phone_number.trim())) {
            newErrors.phone_number = getLocaleContent(locale, 'page.booking-form.validation.phone-max');
        }

        if (!formData.address.trim()) {
            newErrors.address = getLocaleContent(locale, 'page.booking-form.validation.address-required');
        } else if (formData.address.length < 5) {
            newErrors.address = getLocaleContent(locale, 'page.booking-form.validation.address-matches');
        }

        if (formData.birth_of_date.trim()) {
            const birthDate = new Date(formData.birth_of_date);
            if (birthDate > new Date()) {
                newErrors.birth_of_date = getLocaleContent(locale, 'page.booking-form.validation.birth-of-date');
            }
        }

        if (!formData.position_applied_for) {
            newErrors.position_applied_for = getLocaleContent(locale, 'page.booking-form.validation.position');
        }

        if (!cvFile) {
            newErrors.upload_cv = getLocaleContent(locale, 'page.booking-form.validation.cv_alert');
        }

        if (formData.content.trim() && formData.content.length < 10) {
            newErrors.content = getLocaleContent(locale, 'page.booking-form.validation.message-matches');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Xử lý submit form ứng tuyển
     * Gửi dữ liệu và file CV lên server
     */
    const handleSubmit = async e => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        setMessage(null);

        try {
            const recruitmentService = new RecruitmentService();
            const submitData = new FormData();
            submitData.append('recruitment_id', formData.position_applied_for);
            submitData.append('full_name', formData.full_name);
            submitData.append('email', formData.email);
            submitData.append('phone_number', formData.phone_number);
            submitData.append('address', formData.address);
            if (formData.birth_of_date) {
                submitData.append('birth_of_date', formData.birth_of_date);
            }
            if (formData.content) {
                submitData.append('content', formData.content);
            }
            if (cvFile) {
                submitData.append('cv', cvFile);
            }

            const res = await recruitmentService.submitApplication(submitData);

            if (res && res.recruitment_apply_id) {
                setMessage({
                    type: 'success',
                    text: `✅ ${getLocaleContent(locale, 'page.booking-form.validation.submit_success')}`,
                });
                resetForm();
                if (isModal) {
                    setTimeout(() => {
                        onClose();
                        setMessage(null);
                    }, 20000);
                } else {
                    setTimeout(() => setMessage(null), 20000);
                }
            } else {
                const errorMsg = res?.message || getLocaleContent(locale, 'page.booking-form.validation.submit_failed');
                setMessage({
                    type: 'error',
                    text: `❌ ${errorMsg}`,
                });
            }
        } catch (error) {
            const errorMsg = error?.message || getLocaleContent(locale, 'page.booking-form.validation.submit_error');
            setMessage({
                type: 'error',
                text: `❌ ${errorMsg}`,
            });
        } finally {
            setLoading(false);
        }
    };

    const renderFormContent = () => (
        <>
            <h3 className={styles.formTitle}>{getLocaleContent(locale, 'page.recruitment.join_us')}</h3>
            <span className={styles.formSubtitle}>{getLocaleContent(locale, 'page.recruitmentinfo_cv')}</span>
            <form onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                    <input
                        name="full_name"
                        type="text"
                        placeholder={getLocaleContent(locale, 'page.booking-form.full-name') + ' (*)'}
                        value={formData.full_name}
                        onChange={handleChange}
                        className={`${styles.input} ${errors.full_name ? styles.inputError : ''}`}
                    />
                    {errors.full_name && <small className={styles.errorText}>{errors.full_name}</small>}
                </div>
                <div className={styles.inputGroup}>
                    <input
                        name="email"
                        type="email"
                        placeholder={getLocaleContent(locale, 'common.email') + ' (*)'}
                        value={formData.email}
                        onChange={handleChange}
                        className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    />
                    {errors.email && <small className={styles.errorText}>{errors.email}</small>}
                </div>
                <div className={styles.inputGroup}>
                    <input
                        name="phone_number"
                        type="tel"
                        placeholder={getLocaleContent(locale, 'page.booking-form.phone') + ' (*)'}
                        value={formData.phone_number}
                        onChange={handleChange}
                        onKeyDown={handlePhoneKeyDown}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={11}
                        className={`${styles.input} ${errors.phone_number ? styles.inputError : ''}`}
                    />
                    {errors.phone_number && <small className={styles.errorText}>{errors.phone_number}</small>}
                </div>
                <div className={styles.inputGroup}>
                    <input
                        name="address"
                        type="text"
                        placeholder={getLocaleContent(locale, 'page.recruitment.address') + ' (*)'}
                        value={formData.address}
                        onChange={handleChange}
                        className={`${styles.input} ${errors.address ? styles.inputError : ''}`}
                    />
                    {errors.address && <small className={styles.errorText}>{errors.address}</small>}
                </div>
                <div className={styles.inputGroup}>
                    <ConfigProvider locale={viVN}>
                        <DatePicker
                            value={birthDate}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            placeholder={getLocaleContent(locale, 'page.recruitment.birthday')}
                            disabledDate={current => current && current > moment().endOf('day')}
                            className={`${styles.input} ${styles.datePickerInput} ${
                                errors.birth_of_date ? styles.inputError : ''
                            }`}
                            style={{width: '100%'}}
                            allowClear
                            inputReadOnly={false}
                            getPopupContainer={trigger => trigger.parentElement || document.body}
                            popupStyle={{zIndex: 9999}}
                        />
                    </ConfigProvider>
                    {errors.birth_of_date && <small className={styles.errorText}>{errors.birth_of_date}</small>}
                </div>

                <div className={styles.inputGroup}>
                    <select
                        name="position_applied_for"
                        value={formData.position_applied_for}
                        onChange={handleChange}
                        className={`${styles.input} ${styles.selectInput} ${
                            errors.position_applied_for ? styles.inputError : ''
                        }`}
                        disabled={isLoadingRecruitments}>
                        <option value="">-- {getLocaleContent(locale, 'page.recruitment.position')} (*) --</option>
                        {getGroupedRecruitmentOptions().map((group, index) => (
                            <optgroup key={index} label={group.label}>
                                {group.options.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    {errors.position_applied_for && (
                        <small className={styles.errorText}>{errors.position_applied_for}</small>
                    )}
                </div>

                <div className={styles.inputGroup}>
                    <div className={styles.uploadWrapper}>
                        <input
                            name="upload_cv"
                            type="text"
                            placeholder={getLocaleContent(locale, 'page.recruitment.position') + ' (*)'}
                            value={formData.upload_cv}
                            readOnly
                            className={`${styles.input} ${errors.upload_cv ? styles.inputError : ''}`}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={handleFileChange}
                            style={{display: 'none'}}
                        />
                        <button type="button" className={styles.uploadButton} onClick={handleUploadClick}>
                            Upload CV
                        </button>
                    </div>
                    {errors.upload_cv && <small className={styles.errorText}>{errors.upload_cv}</small>}
                </div>

                <div className={styles.inputGroup}>
                    <textarea
                        name="content"
                        className={`${styles.textarea} ${errors.content ? styles.inputError : ''}`}
                        placeholder={getLocaleContent(locale, 'page.booking-form.message') + '(*)'}
                        value={formData.content}
                        onChange={handleChange}></textarea>
                    {errors.content && <small className={styles.errorText}>{errors.content}</small>}
                </div>

                {message && (
                    <p
                        className={`${styles.message} ${
                            message.type === 'error' ? styles.messageError : styles.messageSuccess
                        }`}>
                        {message.text}
                    </p>
                )}

                <button type="submit" className={styles.submitBtn} disabled={loading}>
                    {loading
                        ? getLocaleContent(locale, 'page.booking-form.sending')
                        : getLocaleContent(locale, 'page.booking-form.send')}
                    {loading ? (
                        <Loading3QuartersOutlined
                            spin
                            style={{
                                color: '#fff',
                                fontSize: 16,
                                marginLeft: '8px',
                            }}
                        />
                    ) : (
                        <img src="/bw_image/arrow-long-right-whilte.png" alt="arrow" />
                    )}
                </button>
            </form>
        </>
    );

    if (isModal && !isVisible) return null;

    if (!isModal) {
        return <div className={styles.formContainer}>{renderFormContent()}</div>;
    }

    return (
        <div className={styles.modalWrapper}>
            <div
                className={styles.modalBackdrop}
                onClick={e => {
                    e.stopPropagation();
                    onClose();
                }}
            />
            <div className={`${styles.formContainer} ${styles.modalMode}`} onClick={e => e.stopPropagation()}>
                <div style={{position: 'relative', padding: '20px'}}>
                    <button
                        className={styles.closeBtn}
                        onClick={e => {
                            e.stopPropagation();
                            onClose();
                        }}
                        type="button"
                        aria-label="Đóng form">
                        ×
                    </button>
                    {renderFormContent()}
                </div>
            </div>
        </div>
    );
}
